import { useState } from 'react';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { BrainCircuit, Send, Lightbulb, BookOpen, Briefcase, Code, HelpCircle } from 'lucide-react';
import SEO from '@/components/SEO';

const quickActions = [
  { label: 'Weekly Focus', icon: Lightbulb, prompt: 'What should I focus on this week?' },
  { label: 'Explain Concept', icon: BookOpen, prompt: 'Explain React hooks like I\'m 5' },
  { label: 'Resume Help', icon: Briefcase, prompt: 'How do I describe my MOS experience for a frontend role?' },
  { label: 'Debugging', icon: Code, prompt: 'I\'m stuck on this problem, help me troubleshoot' },
  { label: 'Interview Prep', icon: HelpCircle, prompt: 'Ask me a behavioral interview question' },
];

const mockMessages = [
  {
    role: 'assistant',
    content: 'Welcome to your AI Transition Agent! I\'m here to help you navigate your career transition. I have access to your MOS background, skill progress, and learning plan. How can I assist you today?',
    timestamp: new Date(Date.now() - 3600000)
  },
  {
    role: 'user',
    content: 'What should I focus on this week?',
    timestamp: new Date(Date.now() - 3500000)
  },
  {
    role: 'assistant',
    content: 'Based on your current progress, I recommend focusing on these three objectives this week:\n\n1. **Complete JavaScript Module 3** (4 hours) - You\'re at 60% proficiency and this will unlock React fundamentals\n2. **Start your Task Manager project** (6 hours) - This will reinforce your JS skills and give you a portfolio piece\n3. **Update your resume** (1 hour) - Add your newly acquired HTML/CSS and Git skills\n\nYou\'re making great progress! These tasks align with your 24-week timeline to become job-ready.',
    timestamp: new Date(Date.now() - 3400000)
  }
];

export default function TransitionAgent() {
  const { isMobile } = useSidebar();
  const [messages, setMessages] = useState(mockMessages);
  const [input, setInput] = useState('');

  const handleQuickAction = (prompt: string) => {
    setInput(prompt);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    
    // Add user message
    const newUserMessage = {
      role: 'user' as const,
      content: input,
      timestamp: new Date()
    };
    
    setMessages([...messages, newUserMessage]);
    setInput('');
    
    // Simulate agent response
    setTimeout(() => {
      const agentResponse = {
        role: 'assistant' as const,
        content: 'I\'m processing your request. In a production environment, this would connect to an AI service to provide personalized guidance based on your profile, skills, and learning progress.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1000);
  };

  return (
    <div className="flex flex-col flex-1 h-screen bg-background text-foreground">
      <SEO title="AI Transition Agent - VeteranOS" description="Your AI-powered career transition assistant" />
      
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border p-4 md:p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <BrainCircuit className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-primary">AI Transition Agent</h1>
              <p className="text-xs text-muted-foreground">Your personalized career guide</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="hidden md:flex">
              Context Aware
            </Badge>
            {isMobile && <SidebarTrigger />}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex flex-wrap gap-2">
          {quickActions.map((action, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              onClick={() => handleQuickAction(action.prompt)}
              className="text-xs"
            >
              <action.icon className="h-3 w-3 mr-1" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {messages.map((message, idx) => (
          <div
            key={idx}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <Card
              className={`max-w-[80%] transition-colors duration-200 ${
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card'
              }`}
            >
              <CardContent className="p-4">
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p className={`text-xs mt-2 ${
                  message.role === 'user' 
                    ? 'text-primary-foreground/70' 
                    : 'text-muted-foreground'
                }`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-border p-4 bg-muted/30">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask me anything about your transition..."
            className="min-h-[60px] max-h-[120px] resize-none"
          />
          <Button 
            onClick={handleSend} 
            disabled={!input.trim()}
            className="self-end"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
